'use client';

import { useEffect } from 'react';

/** Only reload once per this window, so a persistent failure cannot loop. */
const RELOAD_THROTTLE_MS = 30000;
const RELOAD_KEY = 'chunk-error-reload';

/** Prefix every asset this deployment serves itself sits under. */
const NEXT_STATIC_PATH = '/_next/static/';

/**
 * The classic post-deploy shape: the chunk request itself failed, and the
 * Next.js/Turbopack runtime says so by name or by wording.
 */
function isChunkLoadFailure(candidate: unknown): boolean {
  const error = candidate as { name?: string; message?: string } | undefined;

  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') === true ||
    error?.message?.includes('Failed to load chunk') === true
  );
}

/** True when `filename` names an asset served by this origin's own build. */
function isFirstPartyNextAsset(filename: string | undefined): boolean {
  if (!filename) return false;

  return (
    filename.startsWith(NEXT_STATIC_PATH) ||
    filename.startsWith(`${window.location.origin}${NEXT_STATIC_PATH}`)
  );
}

/**
 * The parser diagnostics a browser emits when byte 0 of a script body is `<`.
 * Chromium and Safari say "Unexpected token '<'" (the BALLEE-JQ event, Edge 152
 * on Chromium); Firefox says "expected expression, got '<'".
 *
 * An alternation of the KNOWN phrasings, not a search for `<` anywhere in the
 * message, because `<` appears in the diagnostic of genuine syntax defects too —
 * untransformed JSX (`const x = <Component />`) and a malformed relational
 * expression both produce "Unexpected token '<'" in Chromium. A browser whose
 * phrasing is not listed here fails CLOSED: no reload, which leaves the visitor
 * exactly where they were without the guard. That is the safe direction; the
 * unsafe one is reloading every 30 seconds over a syntax bug we shipped.
 *
 * `(?:'<'|<)` rather than `'?<'?`, which made each apostrophe independently
 * optional and so also accepted the half-quoted `'<` and `<'`. And anchored at
 * `^`, so the phrasing must OPEN the message rather than appear somewhere
 * inside it: unanchored, a message merely containing these words would pass.
 * Not anchored at the end, because an engine may append context after the
 * token and that should still count.
 */
const HTML_PARSE_DIAGNOSTICS =
  /^(?:unexpected token (?:'<'|<)|expected expression, got (?:'<'|<))/i;

/**
 * The other way a first-party script dies: it arrives, but what arrives is not
 * JavaScript. A proxy, content filter, captive portal or a transient edge error
 * page answers a `_next/static` request with an HTML document, and the browser
 * reports a parse failure at line 1 column 1 — because byte 0 of the body is
 * `<`. Sentry BALLEE-JQ (BAL-1080) is one such session: `SyntaxError:
 * Unexpected token '<'` on a chunk that serves valid JavaScript to everyone
 * else, from a deployment that had been live and unchanged for 19 hours.
 *
 * None of `isChunkLoadFailure`'s three markers fit that shape, so the page had
 * no route back and the visitor was left on a dead one.
 *
 * Four conjunctive conditions, deliberately — NEVER a bare SyntaxError catch.
 * A genuine syntax bug shipped by the bundler would also be a SyntaxError in a
 * `_next/static` asset, and reloading on that buys a reload every 30 seconds
 * forever in place of a fix.
 *
 * Position is what separates the two, and it is why `lineno`/`colno` are
 * conditions rather than commentary. An HTML document fails at 1:1, because its
 * very first byte is the `<` the parser chokes on. A JSX or relational-operator
 * defect inside a minified bundle carries a `<` too, but at the column where
 * that token sits — never at column 1, since a bundle cannot begin with the
 * offending token and still be a bundle. Matching the message alone would
 * recover the HTML case and reload-loop on the bundler case; matching 1:1 as
 * well separates them.
 *
 * The strictness costs the variant where the HTML body opens with whitespace or
 * a newline, which the browser would report at 2:1. Accepted knowingly: no such
 * event has been observed, an error page's first byte is `<!DOCTYPE` or `<html`
 * in every sample measured, and failing closed only returns the visitor to the
 * pre-guard behaviour whereas failing open loops.
 *
 * What a reload does and does not buy: it retries, which recovers the transient
 * interpositions and the transient edge error, and achieves nothing against a
 * filter that blocks the URL every time. That case costs one extra page load
 * and then stops, because the throttle below bounds it — the same trade the
 * ChunkLoadError path above has always made.
 */
function isHtmlServedAsScript(
  candidate: unknown,
  filename: string | undefined,
  lineNumber: number,
  columnNumber: number,
): boolean {
  const error = candidate as { name?: string; message?: string } | undefined;

  if (error?.name !== 'SyntaxError') return false;
  if (lineNumber !== 1 || columnNumber !== 1) return false;
  if (!HTML_PARSE_DIAGNOSTICS.test(error.message ?? '')) return false;

  return isFirstPartyNextAsset(filename);
}

/** Reload at most once per throttle window, so a stuck page cannot spin. */
function reloadOnce(): void {
  const lastReload = window.sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();

  if (lastReload && now - parseInt(lastReload, 10) <= RELOAD_THROTTLE_MS) {
    return;
  }

  window.sessionStorage.setItem(RELOAD_KEY, now.toString());

  // Force a hard reload to get fresh assets from server
  window.location.reload();
}

/**
 * Global handler for the two ways a first-party chunk leaves the page
 * unrecoverable: the request fails (ChunkLoadError, typically after a deploy
 * changed the hashes), or the response arrives and is not JavaScript.
 *
 * Either way the module graph never registers, so nothing inside the app can
 * repair it — a reload is the only route back.
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (
        isChunkLoadFailure(event.error) ||
        isHtmlServedAsScript(
          event.error,
          event.filename,
          event.lineno,
          event.colno,
        )
      ) {
        reloadOnce();
      }
    };

    // Handle unhandled promise rejections (dynamic imports throw these).
    // The HTML-served-as-script check is deliberately NOT applied here: a
    // rejection carries no `filename`, so the first-party condition that keeps
    // the guard narrow cannot be evaluated.
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadFailure(event.reason)) {
        reloadOnce();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
